import type { AccessSettings, PersonPage } from '@ralph/contracts'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  approveGrant,
  deactivatePerson,
  fetchAccessSettings,
  fetchDeactivationPreview,
  fetchPendingGrants,
  fetchPeople,
  fetchPeopleCounts,
  fetchPerson,
  fetchSignInFailures,
  type GrantInput,
  grantRole,
  type PeopleFilters,
  preparePendingGrant,
  reactivatePerson,
  revokeRole,
  revokeSessions,
  saveAccessSettings,
  withdrawPendingGrant
} from '@/api/people.ts'

/**
 * Queries for the people plane (PRD-21).
 *
 * Every mutation invalidates BOTH the list and the open profile, because an access change moves
 * two things a reader is looking at simultaneously — the row's chips and the panel's provenance —
 * and a surface where one updates and the other does not is a surface that gets distrusted at
 * exactly the moment it matters most.
 *
 * The counts strip is invalidated too: "Admins 2" going stale after a grant is the difference
 * between a number an administrator can act on and a number they have to re-check elsewhere.
 */

export const peopleKeys = {
  list: (filters: PeopleFilters) => ['people', 'list', filters] as const,
  counts: ['people', 'counts'] as const,
  person: (id: string) => ['people', 'person', id] as const,
  preview: (id: string) => ['people', 'preview', id] as const,
  pending: ['people', 'pending'] as const,
  failures: ['people', 'sign-in-failures'] as const,
  settings: ['people', 'settings'] as const
}

export const usePeople = (filters: PeopleFilters, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: peopleKeys.list(filters),
    queryFn: ({ pageParam }: { pageParam: string | null }) => fetchPeople(filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: PersonPage) => last.nextCursor,
    enabled,
    staleTime: 15_000
  })

export const usePeopleCounts = (enabled: boolean) =>
  useQuery({ queryKey: peopleKeys.counts, queryFn: fetchPeopleCounts, enabled, staleTime: 15_000 })

export const usePerson = (id: string | null) =>
  useQuery({ queryKey: peopleKeys.person(id ?? ''), queryFn: () => fetchPerson(id ?? ''), enabled: id !== null })

/** Fetched when the dialog opens, never with the row: it is four aggregate queries. */
export const useDeactivationPreview = (id: string | null) =>
  useQuery({
    queryKey: peopleKeys.preview(id ?? ''),
    queryFn: () => fetchDeactivationPreview(id ?? ''),
    enabled: id !== null,
    staleTime: 0
  })

export const usePendingGrants = (enabled: boolean) =>
  useQuery({ queryKey: peopleKeys.pending, queryFn: fetchPendingGrants, enabled })

export const useSignInFailures = (enabled: boolean) =>
  useQuery({ queryKey: peopleKeys.failures, queryFn: fetchSignInFailures, enabled })

export const useAccessSettings = (enabled: boolean) =>
  useQuery({ queryKey: peopleKeys.settings, queryFn: fetchAccessSettings, enabled })

/** One place decides what an access change invalidates, so no mutation can forget half of it. */
const useAccessInvalidation = () => {
  const client = useQueryClient()
  return (userId?: string) => {
    void client.invalidateQueries({ queryKey: ['people', 'list'] })
    void client.invalidateQueries({ queryKey: peopleKeys.counts })
    void client.invalidateQueries({ queryKey: peopleKeys.pending })
    if (userId) {
      void client.invalidateQueries({ queryKey: peopleKeys.person(userId) })
      void client.invalidateQueries({ queryKey: peopleKeys.preview(userId) })
    }
    // The signed-in reader may have just changed their own access, and the shell's role-gated
    // navigation hangs off this query.
    void client.invalidateQueries({ queryKey: ['session'] })
  }
}

export const useGrantRole = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: ({ userId, ...input }: GrantInput & { userId: string }) => grantRole(userId, input),
    onSuccess: (_result, variables) => invalidate(variables.userId)
  })
}

export const useRevokeRole = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: {
      userId: string
      role: Parameters<typeof revokeRole>[1]
      effect: 'allow' | 'deny'
      reason: string
    }) => revokeRole(input.userId, input.role, input.effect, input.reason),
    onSuccess: (_result, variables) => invalidate(variables.userId)
  })
}

export const useApproveGrant = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: { userId: string; grantId: string }) => approveGrant(input.userId, input.grantId),
    onSuccess: (_result, variables) => invalidate(variables.userId)
  })
}

export const useDeactivatePerson = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: { userId: string; reason: string; confirmName?: string }) =>
      deactivatePerson(input.userId, {
        reason: input.reason,
        ...(input.confirmName ? { confirmName: input.confirmName } : {})
      }),
    onSuccess: (_result, variables) => invalidate(variables.userId)
  })
}

export const useReactivatePerson = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: { userId: string; reason: string }) => reactivatePerson(input.userId, input.reason),
    onSuccess: (_result, variables) => invalidate(variables.userId)
  })
}

export const useRevokeSessions = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: { userId: string }) => revokeSessions(input.userId),
    onSuccess: (_result, variables) => invalidate(variables.userId)
  })
}

export const usePrepareGrant = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({
    mutationFn: (input: Parameters<typeof preparePendingGrant>[0]) => preparePendingGrant(input),
    onSuccess: (result) => invalidate(result.userId ?? undefined)
  })
}

export const useWithdrawPendingGrant = () => {
  const invalidate = useAccessInvalidation()
  return useMutation({ mutationFn: (id: string) => withdrawPendingGrant(id), onSuccess: () => invalidate() })
}

export const useSaveAccessSettings = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (settings: AccessSettings) => saveAccessSettings(settings),
    onSuccess: (settings) => client.setQueryData(peopleKeys.settings, settings)
  })
}
